import { useState, useEffect, useContext, useRef, memo } from 'react';
import { useParams } from 'react-router-dom';
import { ReactEditor, useSlate } from 'slate-react';
import request from 'app/api/request';
import { EditorContext, useEditorSelector } from 'app/state/contexts/EditorContext';
import { createPortal } from 'react-dom';
import { refHandler } from 'app/utils/helper-functions';
import { usePopper } from 'react-popper';
import { times } from 'ramda';
import { i18n } from 'app/utils/i18n';
import { useSelector } from 'react-redux';
import { StructurePreview } from 'app/views/documents/settings/page-structure/StructurePreview';
import { Modal } from 'app/components';
import Area from './Area';
import { insertTemplate } from 'app/slate/extensions/page';
import { useQuery } from 'react-query';
import { useDesignDraft } from 'app/views/documents/settings/design-configuration/hooks/useDesignDraft';
import { useOverflowMarker } from './useOverflowMarker';

const PageDivider = ({ index, pageRef, placement, readOnly }) => {
  const editor = useSlate();
  const { sectionId } = useParams();
  const reduxSectionPermissions = useSelector((state) => state.document.sectionPermissions);
  const [popperElement, setPopperElement] = useState(null);
  const [showNewPageModal, setShowNewPageModal] = useState(false);
  const { styles, attributes } = usePopper(pageRef, popperElement, {
    placement,
    modifiers: [
      {
        name: 'flip',
        options: {
          fallbackPlacements: [],
        },
      },
    ],
  });

  const isSectionButtonDisabled = () => {
    return reduxSectionPermissions[sectionId] === 'COMMENT';
  };

  if (readOnly) {
    return <Portal />;
  }

  return (
    <Portal>
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div ref={setPopperElement} style={styles.popper} {...attributes.popper}>
          <div className="page-divider">
            {isSectionButtonDisabled() ? (
              <button disabled className="page-divider--button text-disabled">
                {i18n('doc-editor.page-divider.link.new-page')} <i className="fa-regular fa-file" />
              </button>
            ) : (
              <button className="page-divider--button" onClick={() => setShowNewPageModal(true)}>
                {i18n('doc-editor.page-divider.link.new-page')} <i className="fa-regular fa-file" />
              </button>
            )}
            {<LayoutPicker show={showNewPageModal} editor={editor} index={index} onClose={() => setShowNewPageModal(false)} />}
          </div>
        </div>
      </div>
    </Portal>
  );
};

const emptyExtra = (index) => (
  <div key={index} className="page-wrapper offset-page" contentEditable={false} style={{ userSelect: 'none' }}>
    <div className="page-content"></div>
  </div>
);

export const LayoutPicker = ({ onClose, show, ...rest }) => {
  const templates = useEditorSelector((editor) => editor.templates);
  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState('all');
  const { style, draftEntry } = useDesignDraft({ styleType: 'graphical-variables', parsed: true });
  const docVars = { ...(style.data?.content ?? {}), ...(draftEntry?.style ?? {}) };
  const aspectRatio = `${parseInt(docVars.pageWidth, 10) || 794} / ${parseInt(docVars.pageHeight, 10) || 1123}`;

  const filtered = (templates || []).filter((page) => {
    const matchesSearch = !search || page.name.toLowerCase().includes(search.toLowerCase());
    const matchesScope =
      scopeFilter === 'all' ||
      (scopeFilter === 'global' && !page.organization && !page.documentSuperId) ||
      (scopeFilter === 'organization' && page.organization && !page.documentSuperId);
    return matchesSearch && matchesScope;
  });

  return (
    <Modal titleLabelKey="layout-picker.title" show={show} onClose={onClose} size="xl" scrollable>
      <div className="d-flex align-items-center gap-2 mb-3 sticky-top bg-blue-100 p-2 rounded">
        <input type="text" className="form-control" placeholder={i18n('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="form-select" style={{ width: 'auto' }} value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value)}>
          <option value="all">{i18n('common.all')}</option>
          <option value="global">{i18n('common.global')}</option>
          <option value="organization">{i18n('common.organization')}</option>
        </select>
      </div>
      {filtered.length === 0 ? (
        i18n('theme.structure.no-available')
      ) : (
        <div className="d-flex flex-wrap justify-content-start gap-r-2">
          {filtered.map((template) => (
            <Layout {...rest} onSelect={onClose} template={template} key={template.revisionId} aspectRatio={aspectRatio} />
          ))}
        </div>
      )}
    </Modal>
  );
};

const Layout = ({ editor, index, template, onSelect, aspectRatio }) => {
  const handleChoseLayout = () => {
    insertTemplate(editor, template, index);
    onSelect();
  };
  return (
    <div className="mb-3">
      <StructurePreview template={template} onClick={handleChoseLayout} aspectRatio={aspectRatio} />
    </div>
  );
};

export const SectionName = () => {
  const revisionIdLeft = useQuery().get('revisionL');
  const { documentId, sectionId } = useParams();
  const [sectionL, setSectionL] = useState({});

  useEffect(() => {
    Promise.all([getDocumentRevision(revisionIdLeft, documentId)])
      .then(([documentL]) => {
        return Promise.all([getSectionRevision(documentL, sectionId)]);
      })
      .then(([sectionRevisionL]) => {
        setSectionL(sectionRevisionL);
      });
  }, [revisionIdLeft, documentId, sectionId]);

  return <span className="page-divider-section-name">{sectionL.name}</span>;
};

const Portal = ({ children }) => {
  const { editorContainerRef } = useContext(EditorContext);
  if (editorContainerRef.current == null) return null;
  return createPortal(children, editorContainerRef.current);
};

const getDocumentRevision = (documentRevisionId, documentId) => {
  if (documentRevisionId) {
    return request.get(`/gaby/documents/${documentRevisionId}?compileSass=true`).then((res) => res.data);
  } else {
    return request
      .get(`/gaby/documents?superId=${documentId}&current=true&compileSass=true`)
      .then((res) => res.data)
      .then((documents) => {
        return documents?.[0];
      });
  }
};

const getSectionRevision = (document, sectionSuperId) => {
  const sectionRevisionId = document.content.sections.find((section) => section.superId === sectionSuperId)?.revisionId;
  return request.get(`/gaby/sections/${sectionRevisionId}`).then((res) => res.data);
};

const MemoizedArea = memo(({ pageId, areaConfig }) => <Area pageId={pageId} areaConfig={areaConfig} />);

const Page = ({ attributes, children, element, withButtons, readOnly, extraOffset = 0 }) => {
  const pDocument = useEditorSelector((editor) => editor.pDocument);
  const editor = useSlate();
  const locale = useEditorSelector((editor) => editor.locale);
  const [pageRef, setPageRef] = useState(null);
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [index] = ReactEditor.findPath(editor, element);
  const isExportRender = typeof window !== 'undefined' && window.location.pathname.includes('/pdf/');
  useOverflowMarker(contentRef, containerRef, { enabled: !isExportRender });

  const pageCssClasses = element.class
    ? element.class
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean)
        .join(' ')
    : '';
  return (
    <>
      {times(emptyExtra, extraOffset)}
      <div {...attributes} ref={refHandler(attributes, setPageRef)} className="page-wrapper">
        {withButtons && !index ? <PageDivider readOnly={readOnly} index={index} pageRef={pageRef} placement="top" /> : null}
        {withButtons ? <PageDivider readOnly={readOnly} index={index + 1} pageRef={pageRef} placement="bottom" /> : null}
        <div
          className={`page-content xrp-template-${element.templateSuperId || ''}${pageCssClasses ? ' ' + pageCssClasses : ''}${
            locale ? ' locale-' + locale.toLowerCase() : ''
          }`.trim()}
          id={`id-${element.uuid}`}
          ref={containerRef}
          style={{ position: 'relative' }}
        >
          <div
            contentEditable={false}
            className={`area-header${element.areas?.header?.className ? ' ' + element.areas.header.className : ''}`}
            style={element.areas?.header?.hidden || pDocument?.content?.header === null ? { display: 'none' } : undefined}
          >
            <MemoizedArea pageId={element.uuid} areaConfig={pDocument.content?.header} />
          </div>
          <div className={`area-content${element.areas?.content?.className ? ' ' + element.areas.content.className : ''}`} ref={contentRef}>
            {children}
          </div>
          <div
            contentEditable={false}
            className={`area-footer${element.areas?.footer?.className ? ' ' + element.areas.footer.className : ''}`}
            style={element.areas?.footer?.hidden || pDocument?.content?.footer === null ? { display: 'none' } : undefined}
          >
            <MemoizedArea pageId={element.uuid} areaConfig={pDocument.content?.footer} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Page;
